

const POPUP_WIDTH = 550;
const POPUP_HEIGHT = 450;
const STATUS_RESET_DELAY = 1500;

const POPUP_NETWORKS = {
  facebook: ({ url }) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,

  twitter: ({ url, text }) =>
    `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,

  pinterest: ({ url, media, description }) =>
    `https://www.pinterest.com/pin/create/button/?url=${encodeURIComponent(url)}&media=${encodeURIComponent(
      media,
    )}&description=${encodeURIComponent(description)}`,

  linkedin: ({ url }) => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,

  whatsapp: ({ url, text }) => `https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`,
};

class SocialShare extends HTMLElement {
  constructor() {
    super();

    this.onClick = this.onClick.bind(this);
  }

  connectedCallback() {
    this.removeUnsupportedTriggers();

    this.addEventListener("click", this.onClick);
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.onClick);

    clearTimeout(this.statusTimeout);
  }

  removeUnsupportedTriggers() {
    if (!navigator.share) {
      this.querySelectorAll('[data-share-type="native"]').forEach((trigger) => trigger.remove());
    }

    if (!navigator.clipboard) {
      this.querySelectorAll('[data-share-type="copy"]').forEach((trigger) => trigger.remove());
    }
  }

  onClick(event) {
    const trigger = event.target.closest("[data-share-type]");

    if (!trigger || !this.contains(trigger)) return;

    const type = trigger.dataset.shareType;
    const data = this.getShareData(trigger);

    event.preventDefault();

    switch (type) {
      case "native":
        this.shareNative(data);
        break;

      case "copy":
        this.copyLink(data);
        break;

      default: {
        const buildUrl = POPUP_NETWORKS[type];

        if (!buildUrl) return;

        this.openPopup(buildUrl(data), `${type}_window`);
      }
    }

    this.dispatchEvent(
      new CustomEvent("social-share:share", {
        bubbles: true,
        detail: { network: type, ...data },
      }),
    );
  }

  getShareData(trigger) {
    const { url, text, media, description } = trigger.dataset;

    const shareUrl = url || window.location.href;
    const shareText = text || document.title;

    return {
      url: shareUrl,
      text: shareText,
      media: media || this.pageImage,
      description: description || shareText,
    };
  }

  get pageImage() {
    return document.querySelector('meta[property="og:image"]')?.content || "";
  }

  async shareNative({ url, text }) {
    try {
      await navigator.share({ title: text, text, url });
    } catch {

    }
  }

  async copyLink({ url }) {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      return;
    }

    this.setStatus(this.dataset.copied);
  }

  setStatus(message) {
    const status = this.querySelector("[data-share-status]");

    if (!status || !message) return;

    status.textContent = message;

    clearTimeout(this.statusTimeout);

    this.statusTimeout = setTimeout(() => {
      status.textContent = "";
    }, STATUS_RESET_DELAY);
  }

  openPopup(url, name) {

    const screenLeft = window.screenLeft ?? window.screenX;
    const screenTop = window.screenTop ?? window.screenY;

    const left = Math.round(screenLeft + (window.innerWidth - POPUP_WIDTH) / 2);
    const top = Math.round(screenTop + (window.innerHeight - POPUP_HEIGHT) / 2);

    const features = `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},top=${top},left=${left},toolbar=0,location=0,menubar=0,directories=0,scrollbars=0`;

    const popup = window.open(url, name, features);

    if (!popup) {
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }

    popup.focus();
  }
}

if (!customElements.get("social-share")) {
  customElements.define("social-share", SocialShare);
}
